/**
 * Framework-agnostic PixiJS world controller. Builds the backdrop, the three
 * coverage objects (house / car / suitcases), and the main character, drives one
 * shared ticker, and exposes a small imperative API the React wrapper pushes
 * props into. Day/night is a single eased value with a full-scene night tint.
 *
 * The "person" zone has no static object — it's the character, which doubles as
 * the login affordance when idle at home.
 */
import type { Application, Ticker } from 'pixi.js'
import { Container, Graphics } from 'pixi.js'
import { buildBackdrop } from './background'
import {
  buildHouse,
  buildCar,
  buildSuitcases,
  buildLife,
  buildPet,
  type SceneObject,
} from './objects'
import { createCharacter } from './character'
import { createFireTruck } from './firetruck'
import { VIRTUAL_W as W, VIRTUAL_H as H, type ZoneKey } from '../sceneLayout'
import type { ZoneCoverage } from '../zoneState'
import { PALETTE as P } from './palette'

// Paint order: house (back) → life/reaper → car → suitcases → cat-tree.
// Character is added last (front-most).
const COVERAGE_ZONES = ['house', 'life', 'vehicle', 'travel', 'pet'] as const
type CoverageZone = (typeof COVERAGE_ZONES)[number]

export interface PixelWorld {
  setCoverage(zone: ZoneKey, coverage: ZoneCoverage): void
  setHovered(zone: ZoneKey | null): void
  /** Marks the zone the character is currently engaging (drives the cat hiss). */
  setActiveZone(zone: ZoneKey | null): void
  /** Fades the uninsured art of a zone away (suitcases carried off by the plane). */
  setGone(zone: ZoneKey, gone: boolean): void
  setDaytime(day: boolean): void
  setCharacter(mode: 'idle' | 'scan' | 'die' | 'fly' | 'wave', x: number): void
  /** Drive the fire-brigade truck on/off-screen (gag for clicking the burning car). */
  commandFireTruck(mode: 'idle' | 'extinguish' | 'leave'): void
  /** Number of insured zones (0..5) — drives the character's accessory ladder. */
  setCharacterLevel(level: number): void
  destroy(): void
}

function isCoverageZone(z: ZoneKey): z is CoverageZone {
  return (
    z === 'house' ||
    z === 'life' ||
    z === 'vehicle' ||
    z === 'travel' ||
    z === 'pet'
  )
}

export function createPixelWorld(app: Application): PixelWorld {
  const stage = new Container()
  stage.label = 'world'
  app.stage.addChild(stage)

  const backdrop = buildBackdrop()
  stage.addChild(backdrop.container)

  // Vehicle gets a richer interface (`setDousing`) the fire truck drives below;
  // keep it typed separately so we don't lose that on the shared record.
  const vehicleObj = buildCar()
  const objects: Record<CoverageZone, SceneObject> = {
    house: buildHouse(),
    life: buildLife(),
    vehicle: vehicleObj,
    travel: buildSuitcases(),
    pet: buildPet(),
  }
  for (const z of COVERAGE_ZONES) stage.addChild(objects[z].container)

  // Fire truck sits in front of the foreground objects but behind the character
  // (the character can walk past / in front of it without being occluded).
  const fireTruck = createFireTruck()
  stage.addChild(fireTruck.container)

  const character = createCharacter()
  stage.addChild(character.container)

  // Full-scene night tint (topmost) — fades out as the sun rises.
  const night = new Graphics()
  night.rect(0, 0, W, H).fill({ color: P.nightTint, alpha: 1 })
  night.alpha = 0.26
  stage.addChild(night)

  let time = 0
  let dayCur = 0
  let dayTarget = 0

  const onTick = (t: Ticker) => {
    const dt = t.deltaMS
    time += dt
    dayCur += (dayTarget - dayCur) * Math.min(1, dt / 700)
    backdrop.setDay(dayCur)
    night.alpha = (1 - dayCur) * 0.26
    backdrop.tick(dt)
    for (const z of COVERAGE_ZONES) objects[z].tick(dt, time)
    fireTruck.update(dt, time)
    // Each frame: the truck publishes its spray amount; the car uses it to
    // scale the flames down (and back up if the truck has left).
    vehicleObj.setDousing(fireTruck.sprayAmount())
    character.update(dt, time)
  }
  app.ticker.add(onTick)

  return {
    setCoverage(zone, coverage) {
      if (isCoverageZone(zone)) objects[zone].setCoverage(coverage)
      // The parked Q400 in the background appears once travel is insured.
      if (zone === 'travel') backdrop.setPlaneVisible(coverage !== 'missing')
    },
    setHovered(zone) {
      for (const z of COVERAGE_ZONES) objects[z].setHover(z === zone)
    },
    setActiveZone(zone) {
      for (const z of COVERAGE_ZONES) objects[z].setActive(z === zone)
    },
    setGone(zone, gone) {
      if (isCoverageZone(zone)) objects[zone].setGone(gone)
    },
    setDaytime(day) {
      dayTarget = day ? 1 : 0
    },
    setCharacter(mode, x) {
      character.command(mode, x)
    },
    commandFireTruck(mode) {
      fireTruck.command(mode)
    },
    setCharacterLevel(level) {
      character.setLevel(level)
    },
    destroy() {
      app.ticker.remove(onTick)
      stage.destroy({ children: true })
    },
  }
}
